import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { shallowEqual } from 'react-redux'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  CATALOG_SOURCE,
  SYNC_URLS,
  SYNC_WINDOWS,
  DivergencesScreen,
  priceRulesFor,
  rowToggled,
  rowsCleared,
  rowsSet,
  shownError,
  sourceOpened,
  syncApply,
  syncCreateTemplate,
  syncDivergences,
  syncIgnore,
  syncPriceRulesSave,
  targetToggled,
  type ApplyAction,
  type ApplyActions,
  type ApplyVm,
  type CreateOptions,
  type Divergence,
  type DivergenceFilter,
  type DivergencesActions,
  type IgnoreCommand,
  type IgnoreLevel,
  type PriceTransfer,
  type ShownError,
  type SourceValue,
  type TargetTemplateVm,
} from '@krp/sync-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { queryState, useOperationIds, useSyncSources } from './syncKit'

type FieldValues = Readonly<Record<string, Readonly<Record<string, string>>>>

const NO_FILTER: DivergenceFilter = { type: null, dataKind: null, query: '' }

const withValue = (values: FieldValues, target: string, key: string, value: string): FieldValues => ({ ...values, [target]: { ...values[target], [key]: value } })

/** Перенос создаёт запись в цели у новой позиции и у позиции только из каталога; «вернуть» создаёт её заново в этом источнике. */
const createsRecord = (row: Divergence, action: ApplyAction): boolean => action === 'restore' || (action === 'copy' && (row.type === 'added' || row.type === 'only-common'))

/**
 * Шаблоны создания целевых источников (план 3.7): по запросу на источник через службу синхронизации, пока открыто окно применения. Число
 * целей меняется, поэтому подписки ведутся командами кэша, а не хуком на каждый источник.
 */
function useCreateTemplates(targets: readonly string[], active: boolean) {
  const dispatch = useAppDispatch()
  const key = targets.join('|')
  const args = useMemo(() => (key === '' ? [] : key.split('|').map((resourceId) => ({ resourceId }))), [key])
  useEffect(() => {
    if (!active || args.length === 0) return
    const subscriptions = args.map((arg) => dispatch(syncCreateTemplate.initiate(arg)))
    return () => {
      for (const subscription of subscriptions) subscription.unsubscribe()
    }
  }, [dispatch, args, active])
  const selectors = useMemo(() => args.map((arg) => syncCreateTemplate.select(arg)), [args])
  return useAppSelector((state) => selectors.map((select) => select(state)), shallowEqual)
}

/**
 * Окно расхождений источника или общего каталога (RP-SYN8). Данные сервера — только в кэше запросов; отметки строк и источники-цели —
 * в слайсе модуля (намерение человека); фильтр, действие и поля применения — в состоянии окна. Отметки действуют в пределах
 * показанного списка: смена фильтра или страницы снимает их, чтобы применение не задело невидимые строки.
 */
export function SyncDivergencesBinding() {
  const { params } = useWindow()
  const source = params.source ?? ''
  const catalog = source === CATALOG_SOURCE
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const selection = useAppSelector((state) => state.sync.selection)
  const { sources, nameOf } = useSyncSources()
  const { operationFor, settled } = useOperationIds()

  useEffect(() => {
    dispatch(sourceOpened(source))
  }, [dispatch, source])

  const [filter, setFilter] = useState<DivergenceFilter>(NO_FILTER)
  const [page, setPage] = useState(1)
  const query = useDeferredValue(filter.query)
  const args = useMemo(() => ({ source, type: filter.type, dataKind: filter.dataKind, query, page }), [source, filter.type, filter.dataKind, query, page])
  const list = syncDivergences.useQuery(args, { skip: source === '' })
  const data = list.currentData
  const items = useMemo(() => data?.items ?? [], [data])

  // Отметки — только из показанного списка (слайс мог остаться от этого же источника до перечитывания).
  const own = selection.source === source
  const selectedIds = useMemo(() => new Set(own ? selection.ids.filter((id) => items.some((item) => item.id === id)) : []), [own, selection.ids, items])
  const rows = items.filter((item) => selectedIds.has(item.id))
  // Строки фото (RP-SYN7): цели — только стороны этих строк — источник без фото в них не стоит, а общий каталог со своим S3 стоит.
  const photoOnly = rows.length > 0 && rows.every((row) => row.dataKind === 'photo')
  const targetChoices = photoOnly
    ? [...new Set(rows.flatMap((row) => row.values.map((value) => value.resourceId)))]
        .filter((resourceId) => resourceId !== source)
        .map((resourceId) => ({ resourceId, name: nameOf(resourceId) }))
    : sources.filter((item) => item.resourceId !== source).map((item) => ({ resourceId: item.resourceId, name: item.displayName }))
  const targets = own ? selection.targets.filter((id) => targetChoices.some((choice) => choice.resourceId === id)) : []

  // «Не отслеживать»: по команде на пару «строка × источник», по одной за раз.
  const [ignore] = syncIgnore.useMutation()
  const [ignoreLevel, setIgnoreLevel] = useState<IgnoreLevel>('record')
  const [ignoreProgress, setIgnoreProgress] = useState<{ done: number; total: number } | null>(null)
  const [ignoreErrors, setIgnoreErrors] = useState<ShownError[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  // Применение.
  const [applyOpen, setApplyOpen] = useState(false)
  const [chosenAction, setChosenAction] = useState<ApplyAction>('copy')
  const [fromChoice, setFromChoice] = useState<string | null>(null)
  const [priceTransfer, setPriceTransfer] = useState<PriceTransfer>('same')
  const [common, setCommon] = useState<FieldValues>({})
  const [perRow, setPerRow] = useState<Readonly<Record<string, FieldValues>>>({})
  const [applyBusy, setApplyBusy] = useState(false)
  const [applyError, setApplyError] = useState<ShownError | null>(null)
  const [apply] = syncApply.useMutation()
  const [saveRules] = syncPriceRulesSave.useMutation()
  const [ruleBusy, setRuleBusy] = useState(false)
  const [ruleError, setRuleError] = useState<ShownError | null>(null)
  const [ruleSaved, setRuleSaved] = useState(false)

  const applyActionsList: ApplyAction[] = [
    'copy',
    ...(rows.some((row) => row.type === 'only-common') ? [] : (['delete'] as const)),
    ...(!catalog && rows.length > 0 && rows.every((row) => row.type === 'removed') ? (['restore'] as const) : []),
  ]
  const action = applyActionsList.includes(chosenAction) ? chosenAction : 'copy'

  const fromChoices = [...new Set(rows.flatMap((row) => row.values.filter((value) => value.present).map((value) => value.resourceId)))]
    .sort((left, right) => (left === source ? -1 : right === source ? 1 : 0))
    .map((resourceId) => ({ resourceId, name: nameOf(resourceId) }))
  const from = fromChoices.some((choice) => choice.resourceId === fromChoice) ? fromChoice : (fromChoices[0]?.resourceId ?? null)

  // Куда уйдёт: у «вернуть» — сам источник окна, иначе отмеченные цели без источника-образца.
  const requestTargets = action === 'restore' ? [source] : targets.filter((target) => !(action === 'copy' && target === from))
  const creating = action === 'restore' || rows.some((row) => createsRecord(row, action))
  const templateResults = useCreateTemplates(requestTargets, applyOpen && creating)
  const templates: TargetTemplateVm[] = requestTargets.map((resourceId, index) => {
    const result = templateResults[index]
    return {
      resourceId,
      name: nameOf(resourceId),
      status: result?.isError === true ? 'failed' : result?.data === undefined ? 'loading' : 'ready',
      error: result?.isError === true ? shownError(`Источник «${nameOf(resourceId)}» не отдал поля создания`, result.error) : null,
      fields: result?.data?.fields ?? [],
    }
  })

  const defaultOf = (target: string, key: string): string => templates.find((template) => template.resourceId === target)?.fields.find((field) => field.key === key)?.defaultValue ?? ''
  const commonValue = (target: string, key: string): string => common[target]?.[key] ?? defaultOf(target, key)
  const rowValue = (rowId: number, target: string, key: string): string => perRow[String(rowId)]?.[target]?.[key] ?? commonValue(target, key)

  const createOptions: CreateOptions[] | null = creating
    ? templates.map((template) => {
        const defaults: Record<string, string> = {}
        for (const field of template.fields) {
          const value = commonValue(template.resourceId, field.key)
          if (value !== '') defaults[field.key] = value
        }
        const ownValues: Record<string, Record<string, string>> = {}
        for (const row of rows.filter((candidate) => createsRecord(candidate, action))) {
          const values = perRow[String(row.id)]?.[template.resourceId]
          if (values === undefined) continue
          const filled = Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''))
          if (Object.keys(filled).length > 0) ownValues[String(row.id)] = filled
        }
        return { resourceId: template.resourceId, defaults, rows: ownValues }
      })
    : null

  const blockers: string[] = []
  if (rows.length === 0) blockers.push('Не отмечено ни одной строки.')
  if (requestTargets.length === 0) blockers.push('Отметьте источники, к которым применить.')
  // Позиции только из общего каталога переносятся из него самого — образец-источник им не нужен.
  if (action === 'copy' && from === null && !catalog) blockers.push('Не выбрано, из какого источника брать значение.')
  if (creating) {
    for (const template of templates) {
      if (template.status === 'loading') blockers.push(`Ждём от «${template.name}» поля, нужные при создании.`)
      if (template.status === 'failed') blockers.push(`«${template.name}» не отдал поля создания — без них создать нельзя.`)
    }
    // Незаполненное обязательное поле — одной строкой на поле источника, с числом строк, а не по строке на каждую позицию.
    const missing = new Map<string, number>()
    for (const row of rows.filter((candidate) => createsRecord(candidate, action))) {
      for (const template of templates) {
        for (const field of template.fields) {
          if (!field.required || rowValue(row.id, template.resourceId, field.key) !== '') continue
          const text = `Не задано обязательное поле «${field.label}» для «${template.name}»`
          missing.set(text, (missing.get(text) ?? 0) + 1)
        }
      }
    }
    for (const [text, count] of missing) blockers.push(`${text}: строк ${count}.`)
  }
  if (applyBusy) blockers.unshift('Применение уже отправлено.')

  const priceRules = action === 'copy' && from !== null ? priceRulesFor(rows, from, requestTargets, priceTransfer) : []

  const closeApply = () => {
    setApplyOpen(false)
    setApplyError(null)
    setRuleError(null)
    setRuleSaved(false)
  }

  const confirm = () => {
    const command = { action, divergenceIds: rows.map((row) => row.id), targets: requestTargets, sourceResourceId: action === 'copy' ? from : null, createOptions }
    const intent = `apply:${JSON.stringify(command)}`
    setApplyBusy(true)
    setApplyError(null)
    // Правило цены — настройка связи: оно сохраняется до переноса, чтобы служба посчитала цену по нему.
    const rules = priceRules.length === 0 ? Promise.resolve() : saveRules({ items: priceRules }).unwrap().then(() => undefined)
    void rules
      .then(() => apply({ ...command, operationId: operationFor(intent) }).unwrap())
      .then((job) => {
        settled(intent)
        dispatch(rowsCleared())
        closeApply()
        setCommon({})
        setPerRow({})
        openWindow(SYNC_WINDOWS.job, { job: job.id })
      })
      .catch((error: unknown) => setApplyError(shownError('Применение не отправлено', error)))
      .finally(() => setApplyBusy(false))
  }

  const saveRuleNow = () => {
    setRuleBusy(true)
    setRuleError(null)
    setRuleSaved(false)
    void saveRules({ items: priceRules })
      .unwrap()
      .then(() => setRuleSaved(true))
      .catch((error: unknown) => setRuleError(shownError('Правило цены не сохранено', error)))
      .finally(() => setRuleBusy(false))
  }

  const applyVm: ApplyVm | null = applyOpen
    ? {
        sourceName: nameOf(source),
        rows,
        targets: requestTargets.map((resourceId) => ({ resourceId, name: nameOf(resourceId) })),
        actions: applyActionsList,
        action,
        fromChoices,
        from,
        nameOf,
        priceTransfer,
        creating,
        templates,
        commonValue,
        rowValue,
        blockers,
        busy: applyBusy,
        error: applyError,
        ruleBusy,
        ruleBlocker: priceRules.length === 0 ? 'У отмеченных цен нет общего товара или не выбраны источник-образец и цели.' : null,
        ruleError,
        ruleSaved,
      }
    : null

  const applyActions: ApplyActions = {
    back: closeApply,
    actionChanged: setChosenAction,
    fromChanged: setFromChoice,
    priceTransferChanged: (transfer) => {
      setPriceTransfer(transfer)
      setRuleSaved(false)
    },
    commonChanged: (target, key, value) => setCommon((current) => withValue(current, target, key, value)),
    rowChanged: (rowId, target, key, value) => setPerRow((current) => ({ ...current, [String(rowId)]: withValue(current[String(rowId)] ?? {}, target, key, value) })),
    confirm,
    saveRule: saveRuleNow,
  }

  /** Команда «не отслеживать» строки для источника; `null` — уровень к строке не подходит (у новой позиции нет общего товара). */
  const ignoreCommand = (row: Divergence, target: string): Omit<IgnoreCommand, 'operationId'> | null => {
    const subject = row.dataKind === 'item' ? null : row.subject
    if (ignoreLevel === 'product') return row.productId === null ? null : { level: 'product', resourceId: target, dataKind: row.dataKind, remoteKey: null, productId: row.productId, subject }
    const record = row.values.find((value) => value.resourceId === target && value.remoteKey !== null) ?? row.values.find((value) => value.resourceId === row.originResourceId && value.remoteKey !== null)
    if (record === undefined || record.remoteKey === null) return null
    return { level: 'record', resourceId: target, dataKind: row.dataKind, remoteKey: record.remoteKey, productId: null, subject }
  }

  const runIgnore = async () => {
    const commands = rows.flatMap((row) => targets.map((target) => ignoreCommand(row, target)))
    const ready = commands.filter((command): command is Omit<IgnoreCommand, 'operationId'> => command !== null)
    const skipped = commands.length - ready.length
    setIgnoreErrors([])
    setNotice(null)
    setIgnoreProgress({ done: 0, total: ready.length })
    const failures: ShownError[] = []
    for (const [index, command] of ready.entries()) {
      const intent = `ignore:${JSON.stringify(command)}`
      try {
        await ignore({ ...command, operationId: operationFor(intent) }).unwrap()
        settled(intent)
      } catch (error: unknown) {
        failures.push(shownError(`Не удалось перестать отслеживать (${nameOf(command.resourceId)})`, error))
      }
      setIgnoreProgress({ done: index + 1, total: ready.length })
    }
    setIgnoreErrors(failures)
    if (failures.length === 0) dispatch(rowsCleared())
    setNotice(
      `Больше не отслеживается: ${ready.length - failures.length}.` +
        (skipped > 0 ? ` Пропущено ${skipped}: у строки нет записи источника (для «Только эта запись») или общего товара (для «Весь товар»).` : '') +
        ' Вернуть — в списке «Не отслеживается».',
    )
  }

  const actions: DivergencesActions = {
    filterChanged: (next) => {
      dispatch(rowsCleared())
      setFilter(next)
      setPage(1)
    },
    pageChanged: (next) => {
      dispatch(rowsCleared())
      setPage(next)
    },
    rowToggled: (id) => dispatch(rowToggled(id)),
    pageSelected: (selected) => dispatch(rowsSet({ ids: items.map((item) => item.id), selected })),
    targetToggled: (resourceId) => dispatch(targetToggled(resourceId)),
    openApply: () => {
      setNotice(null)
      setApplyOpen(true)
    },
    ignoreLevelChanged: setIgnoreLevel,
    ignore: () => void runIgnore(),
    reload: () => void list.refetch(),
  }

  const photoSrc = (value: SourceValue): string | null =>
    value.value === null || value.remoteKey === null ? null : SYNC_URLS.photo(value.resourceId, value.value, value.remoteKey)

  const state = queryState({ ...list, data }, 'Не удалось получить расхождения')
  return (
    <DivergencesScreen
      vm={{
        source,
        sourceName: nameOf(source),
        catalog,
        filter,
        ...state,
        page,
        pages: data === undefined ? 1 : Math.max(1, Math.ceil(data.total / Math.max(1, data.pageSize))),
        total: data?.total ?? 0,
        items,
        nameOf,
        photoSrc,
        selectedIds,
        targetChoices,
        targets,
        ignoreLevel,
        ignoreProgress,
        ignoreErrors,
        notice,
        apply: applyVm,
      }}
      actions={actions}
      apply={applyActions}
    />
  )
}
